import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { ArrowLeft, Search, SlidersHorizontal, X, Star } from 'lucide-react';
import { Input } from '@/components/ui/input';
import BottomNav from '@/components/BottomNav';

const SearchPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('category') || '');
  const [vendors, setVendors] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ veg: false, nonVeg: false, rating: 0, priceRange: 'all' });

  useEffect(() => {
    if (searchQuery) performSearch();
  }, [searchQuery, filters]);

  const performSearch = async () => {
    setLoading(true);
    try {
      const [vendorsRes, menuItemsRes] = await Promise.all([
        axios.get(`${API}/vendors?search=${searchQuery}`),
        axios.get(`${API}/menu-items?search=${searchQuery}`)
      ]);
      let filteredVendors = vendorsRes.data;
      let filteredItems = menuItemsRes.data;
      if (filters.veg) filteredItems = filteredItems.filter(item => item.is_veg);
      if (filters.nonVeg) filteredItems = filteredItems.filter(item => !item.is_veg);
      if (filters.rating > 0) {
        filteredVendors = filteredVendors.filter(v => v.rating >= filters.rating);
        filteredItems = filteredItems.filter(item => item.rating >= filters.rating);
      }
      setVendors(filteredVendors);
      setMenuItems(filteredItems);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3 mb-3">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full"><ArrowLeft className="w-6 h-6" /></button>
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input type="text" placeholder="Search for dishes, vendors..." className="pl-10 pr-10 py-6 rounded-xl" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} autoFocus />
              {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 transform -translate-y-1/2"><X className="w-5 h-5 text-gray-400" /></button>}
            </div>
            <button onClick={() => setShowFilters(!showFilters)} className="p-3 bg-blue-50 text-blue-600 rounded-xl"><SlidersHorizontal className="w-5 h-5" /></button>
          </div>
          {showFilters && (
            <div className="bg-gray-50 rounded-xl p-4 flex flex-wrap gap-2">
              <button onClick={() => setFilters({ ...filters, veg: !filters.veg })} className={`px-4 py-2 rounded-full text-sm font-medium ${filters.veg ? 'bg-green-600 text-white' : 'bg-white border'}`}>Veg Only</button>
              <button onClick={() => setFilters({ ...filters, nonVeg: !filters.nonVeg })} className={`px-4 py-2 rounded-full text-sm font-medium ${filters.nonVeg ? 'bg-red-600 text-white' : 'bg-white border'}`}>Non-Veg</button>
            </div>
          )}
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading ? <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto"></div></div> : (
          <>
            {vendors.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-bold mb-4">Vendors</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {vendors.map(vendor => (
                    <div key={vendor.id} className="food-card bg-white rounded-xl shadow-md cursor-pointer" onClick={() => navigate(`/vendor/${vendor.id}`)}>
                      <div className="h-40 bg-gray-200"><img src={vendor.image_url} alt={vendor.name} className="image-cover" /></div>
                      <div className="p-4"><h3 className="font-bold text-lg">{vendor.name}</h3><p className="text-sm text-gray-600">{vendor.description}</p><div className="flex justify-between mt-2"><span className="text-yellow-500">{vendor.rating} ⭐</span><span className="text-sm text-gray-600">{vendor.delivery_time}</span></div></div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {menuItems.length > 0 && (
              <div>
                <h2 className="text-xl font-bold mb-4">Dishes</h2>
                <div className="space-y-4">
                  {menuItems.map(item => (
                    <div 
                      key={item.id} 
                      className="bg-white rounded-xl shadow-md p-4 flex gap-4 cursor-pointer hover:shadow-lg transition-shadow" 
                      onClick={() => navigate(`/vendor/${item.vendor_id}`)}
                      data-testid={`search-dish-${item.id}`}
                    >
                      {/* Left Side - Text Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {item.is_veg ? (
                            <div className="w-5 h-5 border-2 border-green-600 flex items-center justify-center flex-shrink-0">
                              <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                            </div>
                          ) : (
                            <div className="w-5 h-5 border-2 border-red-600 flex items-center justify-center flex-shrink-0">
                              <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                            </div>
                          )}
                          <h3 className="font-bold text-gray-800 truncate">{item.name}</h3>
                        </div>
                        <p className="text-lg font-bold text-gray-800 mb-1">₹{item.price}</p>
                        <div className="flex items-center gap-1 mb-2">
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                          <span className="text-sm text-gray-600">{item.rating}</span>
                        </div>
                        <p className="text-sm text-gray-500 line-clamp-2">{item.description}</p>
                      </div>
                      {/* Right Side - Image */}
                      <div className="relative flex-shrink-0 w-28 h-28 sm:w-32 sm:h-32">
                        <img 
                          src={item.image_url} 
                          alt={item.name} 
                          className="w-full h-full rounded-xl object-cover" 
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!loading && vendors.length === 0 && menuItems.length === 0 && searchQuery && <div className="text-center py-12"><p className="text-gray-500">No results found for "{searchQuery}"</p></div>}
          </>
        )}
      </div>
      <BottomNav />
    </div>
  );
};

export default SearchPage;
